import React, { useMemo, useCallback, memo } from 'react';

import { Box, Center, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { customTheme } from 'theme';

import { CloseCircleOutlineIcon } from 'components/Icons';
import { FunnelChartProps } from 'components/Visualization/Charts/FunnelChart/types';

// --- Individual Funnel Step Component ---
const FunnelStep = ({
  step,
  onRemove,
  totalValue,
  stepClipPath,
  allowRemovingStep,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step?.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    //flexGrow: step?.value,
    //flexShrink: 1,
    //flexBasis: '0%', // This is key for flex-grow to work proportionally for width
    backgroundColor: step.color,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    clipPath: stepClipPath, // Apply the dynamically calculated clipPath
    height: '100%', // Each step now takes full height of the container
  };

  // Calculate the percentage width for display (still calculated, but not displayed inside the step)
  const percentage =
    totalValue > 0 ? ((step?.value / totalValue) * 100).toFixed(1) : 0;

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      flex="1 1 0%"
      minW={20}
      role="group"
      title={step.title}
      position="relative"
      align="center"
      justify="center"
      cursor={
        allowRemovingStep ? 'grab' : !!step.onClick ? 'pointer' : 'default'
      }
      onClick={() => step.onClick?.()}
    >
      <Flex w="full" className="flex flex-row p-2">
        <Center
          fontSize={{ sm: 'lg', md: 'xl', lg: '2xl' }}
          p={0}
          w="full"
          className="text-xl md:text-2xl lg:text-3xl font-bold"
        >
          {step.value?.toLocaleString()}
        </Center>
        {/* Removed step name and percentage text from inside the funnel step */}
        {/* <span className="text-xs md:text-sm mt-1 hidden sm:block">{step.name}</span> */}
        {/* <span className="text-xs md:text-sm mt-1 block sm:hidden">{`${percentage}%`}</span> */}
      </Flex>
      {/*<Flex justify="flex-end" position="absolute" right={1} top="-50%">*/}
      {allowRemovingStep && (
        <Center
          position="absolute"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          _hover={{ opacity: 1 }}
          transition="opacity 0.2s ease"
          color={customTheme.colors.gray['300']}
          right={0}
          onClick={e => {
            e.stopPropagation();
            onRemove(step);
          }}
          aria-label={`Remove ${step.name}`}
          p={1}
          w={7}
          h={7}
          borderRadius="full"
          bg="white"
          cursor="pointer"
        >
          <CloseCircleOutlineIcon />
        </Center>
      )}
      {/*</Flex>*/}
    </Flex>
  );
};

// --- Main App Component ---
export const FunnelChart = memo((props: FunnelChartProps) => {
  const {
    activeSteps,
    setActiveStepIds,
    allowRemovingSteps = false,
    activeStepIds,
  } = props;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Memoize total value to avoid recalculating on every render
  const totalValue = useMemo(
    () => activeSteps.reduce((sum, step) => sum + step.value, 0),
    [activeSteps],
  );

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      if (active.id !== over.id) {
        setActiveStepIds?.(steps => {
          const oldIndex = steps.findIndex(s => s === active.id);
          const newIndex = steps.findIndex(s => s === over.id);
          return arrayMove(steps, oldIndex, newIndex);
        });
      }
    },
    [setActiveStepIds],
  );

  const handleRemoveStep = useCallback(
    step => {
      const stepToRemove = activeStepIds?.find(s => s === step.id);
      if (stepToRemove) {
        setActiveStepIds?.(prev => prev.filter(s => s !== step.id));
      }
    },
    [setActiveStepIds],
  );

  /*const handleAddStep = stepId => {
    const stepToAdd = removedSteps.find(s => s.id === stepId);
    if (stepToAdd) {
      setRemovedSteps(prev => prev.filter(s => s.id !== stepId));
      setActiveSteps(prev => [...prev, stepToAdd]);
    }
  };*/

  // Define the overall funnel's top and bottom slopes
  // These values are percentages of the container's height
  const overallFunnelTopStart = 0; // Top edge starts at 0% from the top
  const overallFunnelTopEnd = 40; // Top edge ends at 40% from the top
  const overallFunnelBottomStart = 100; // Bottom edge starts at 100% from the top (i.e., bottom)
  const overallFunnelBottomEnd = 60; // Bottom edge ends at 60% from the top

  let currentCumulativeValue = 0; // To track cumulative value for clipPath calculation

  const renderFunnel = () => (
    <HStack flex={1} w="full" spacing={1} h="80%">
      {activeSteps.length > 0 ? (
        <SortableContext
          items={activeSteps.map(s => s.id)}
          strategy={rectSortingStrategy}
        >
          {activeSteps.map((step, index) => {
            // Calculate cumulative width ratio for this step's start and end
            const cumulativeWidthRatioStart =
              totalValue > 0 ? currentCumulativeValue / totalValue : 0;
            currentCumulativeValue += step.value; // Update cumulative value for the next step
            const cumulativeWidthRatioEnd =
              totalValue > 0 ? currentCumulativeValue / totalValue : 0;

            // Calculate Y coordinates for clipPath based on overall funnel slope
            const topYLeft =
              overallFunnelTopStart +
              (overallFunnelTopEnd - overallFunnelTopStart) *
                cumulativeWidthRatioStart;
            const bottomYLeft =
              overallFunnelBottomStart +
              (overallFunnelBottomEnd - overallFunnelBottomStart) *
                cumulativeWidthRatioStart;
            const topYRight =
              overallFunnelTopStart +
              (overallFunnelTopEnd - overallFunnelTopStart) *
                cumulativeWidthRatioEnd;
            const bottomYRight =
              overallFunnelBottomStart +
              (overallFunnelBottomEnd - overallFunnelBottomStart) *
                cumulativeWidthRatioEnd;

            const stepClipPath = `polygon(0% ${topYLeft}%, 100% ${topYRight}%, 100% ${bottomYRight}%, 0% ${bottomYLeft}%)`;

            return (
              <FunnelStep
                key={step.id}
                step={step}
                onRemove={handleRemoveStep}
                totalValue={totalValue}
                stepClipPath={stepClipPath} // Pass the calculated clipPath
                allowRemovingStep={allowRemovingSteps}
              />
            );
          })}
        </SortableContext>
      ) : (
        <Center w="full" h="full" color={customTheme.colors.gray['250']}>
          Funnel is empty. Add steps from the dropdown.
        </Center>
      )}
    </HStack>
  );

  return (
    <Box
      h="full"
      position="relative"
      className="bg-gray-50 min-h-screen w-full flex flex-col items-center justify-center p-4 font-sans"
    >
      {/*<style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
        body {
          font-family: 'Inter', sans-serif;
        }
        `}
      </style>*/}
      <Stack flex={1} w="full" h="full">
        {allowRemovingSteps ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {renderFunnel()}
          </DndContext>
        ) : (
          renderFunnel()
        )}

        {/* Labels below the funnel */}
        <HStack w="full" spacing={2} h="20%">
          {activeSteps.map(step => (
            <Center key={step.id} w="full" textAlign="center">
              <Text fontSize="sm" noOfLines={2}>
                {step?.label ?? step.name}
              </Text>
            </Center>
          ))}
        </HStack>

        {/* Removed Steps Panel */}
        {/*<Box h="10%">
          {removedSteps.length > 0 && (
            <div className="mt-12 w-full">
              <h2 className="text-xl font-semibold text-gray-700 text-center mb-4">
                Available Steps
              </h2>
              <Wrap className="flex flex-wrap gap-4 justify-center">
                {removedSteps.map(step => (
                  <WrapItem key={step.id}>
                    <HStack
                      py={2}
                      px={3}
                      borderRadius={6}
                      cursor="pointer"
                      onClick={() => handleAddStep(step.id)}
                      className="relative p-3 pl-8 rounded-lg shadow-sm flex items-center"
                      style={{ backgroundColor: step.color }}
                    >
                      <Box
                        color={customTheme.colors.green['300']}
                        p={0.5}
                        borderRadius="full"
                        bg="white"
                        //className="absolute left-1 top-1/2 -translate-y-1/2 p-1 bg-white/60 rounded-full text-green-600 hover:bg-white transition-colors"
                        aria-label={`Add ${step.name}`}
                      >
                        <PlusCircle size={18} />
                      </Box>
                      <HStack spacing={1}>
                        <Text fontWeight={600} className="font-bold">
                          {step.value}
                        </Text>
                        <Text>-</Text>
                        <Text>{step.name}</Text>
                      </HStack>
                    </HStack>
                  </WrapItem>
                ))}
              </Wrap>
            </div>
          )}
        </Box>*/}
      </Stack>
    </Box>
  );
});

export default FunnelChart;
